/**
 * Visualizer Controls
 * Handles visualization type selector and dynamic controls UI
 */

import { lyricsState, playlistState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { VisualizerRegistry } from '../visualizer.js';
import { entitlementService } from '../entitlements/entitlement-service.js';
import { featureGate } from '../entitlements/feature-gate.js';
import { closePanelOnSelect, getActivePanel } from './overlay-panels.js';
import { thumbnailService } from '../lib/thumbnail-service.js';
import { thumbnailGenerator } from '../lib/thumbnail-generator.js';
import { visualizerLoader } from '../lib/visualizer-loader.js';
import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL } from '../config/config.js';
import { escapeHtml, escapeAttr } from '../lib/html-utils.js';
import {
  initCustomVisualizer,
  showModal as showCustomVizModal,
  showEditModal,
  deleteVisualizer,
  fetchVisualizerCode
} from './custom-visualizer.js';
import { trackVisualizerSelected } from './analytics/event-tracker.js';
import { extractConfigDefaults } from '../lib/audio-utils.js';

/** @type {Map<string, number>} Map of visualizer names to hover timeout IDs */
const hoverTimeouts = new Map();

/** @type {Set<string>} Set of visualizer names currently being hovered */
const hoveredCards = new Set();

/** @type {IntersectionObserver|null} Observer for lazy loading thumbnails */
let thumbnailObserver = null;

/** @type {boolean} Whether thumbnail loading has been initialized */
let thumbnailsInitialized = false;

/** @type {boolean} Whether admin reorder mode is active */
let reorderMode = false;

/** @type {Element|null} Currently dragged card during reorder */
let draggedCard = null;

/** @type {Set<Element>} Selected cards for multi-select drag in reorder mode */
const selectedCards = new Set();

/** @type {Element|null} Anchor card for shift+click range selection */
let selectionAnchor = null;

/** @type {Element[]} Elements being observed before pause */
let _pausedObserverElements = [];

/**
 * Pause the thumbnail observer during export to prevent GPU contention.
 * The observer generates thumbnails (potentially with WebGPU) when cards become visible.
 */
export function pauseThumbnailObserver() {
  if (!thumbnailObserver) return;

  // Store currently observed elements so we can resume later
  // Unfortunately, IntersectionObserver doesn't expose observed elements,
  // so we just disconnect. When resumed, cards already visible will re-trigger.
  thumbnailObserver.disconnect();
  console.log('[VisualizerControls] Thumbnail observer paused for export');
}

/**
 * Resume the thumbnail observer after export completes.
 * Note: This won't re-observe previously disconnected elements,
 * but new lazy loading will work if the user opens the selector panel.
 */
export function resumeThumbnailObserver() {
  // Observer is still valid, just disconnected
  // Re-setup will happen next time the panel is opened via setupThumbnailLazyLoading()
  console.log('[VisualizerControls] Thumbnail observer can be resumed (will re-init on panel open)');
}

/**
 * Populate visualization type selector with available visualizers
 * Note: Does NOT set a default visualizer on initial load. The visualizer
 * is set lazily when the user starts playback, enters live mode, or
 * explicitly selects a visualizer. This prevents rendering until needed.
 */
export async function populateVizSelector() {
  await VisualizerRegistry.fetchHiddenSet();
  const vizList = VisualizerRegistry.getListSync();
  const allowedViz = await entitlementService.getAllowedVisualizers();
  const allAllowed = allowedViz.includes('*'); // Free mode - all visualizers unlocked

  // Check if a visualizer is already active (user has made a selection)
  const currentType = managers.visualizerManager?.getType();
  const hasActiveVisualizer = !!currentType;

  // Skip if legacy dropdown doesn't exist (new UI uses grid)
  if (!elements.vizTypeSelect) {
    // Don't set a default visualizer - wait for user action (playback, live mode, or explicit selection)
    return;
  }

  elements.vizTypeSelect.innerHTML = vizList.map(viz => {
    const isLocked = !allAllowed && !allowedViz.includes(viz.name);
    const displayName = isLocked ? `${viz.displayName} 🔒` : viz.displayName;
    return `<option value="${viz.name}" data-locked="${isLocked}">${displayName}</option>`;
  }).join('');

  // Don't set a default visualizer - wait for user action
  // Only sync dropdown with current visualizer if one exists
  if (hasActiveVisualizer && elements.vizTypeSelect) {
    elements.vizTypeSelect.value = currentType;
  }
}

/**
 * Build dynamic controls UI based on selected visualization
 */
export function buildControlsUI() {
  const configUI = managers.visualizerManager.getConfigUI();
  const currentConfig = managers.visualizerManager.getConfig();

  // Group controls by section
  const sections = {};
  configUI.forEach(control => {
    const sectionName = control.section || 'General';
    if (!sections[sectionName]) {
      sections[sectionName] = [];
    }
    sections[sectionName].push(control);
  });

  // Build HTML for each section as accordion
  const sectionsHTML = Object.entries(sections).map(([sectionName, controls], index) => {
    const controlsHTML = controls.map(control => {
      const value = currentConfig[control.id] !== undefined ? currentConfig[control.id] : control.default;

      switch (control.type) {
        case 'range':
          // Display value is the actual config value (e.g., 0.30)
          const displayValue = control.scale ? value.toFixed(2) : value;
          // Slider value needs to be in slider units (actual / scale, e.g., 0.30 / 0.01 = 30)
          const sliderValue = control.scale ? Math.round(value / control.scale) : value;

          // Audio mapping controls for mappable parameters
          let mappingHTML = '';
          if (control.mappable) {
            const mappingKey = `${control.id}Mapping`;
            const mappingConfig = currentConfig[mappingKey] || control.mappingDefault || {};
            const isEnabled = mappingConfig.enabled || false;
            const source = mappingConfig.source || 'bands.bass';
            const srcMin = mappingConfig.sourceMin ?? 0;
            const srcMax = mappingConfig.sourceMax ?? 1;
            const tgtMin = mappingConfig.targetMin ?? (control.min * (control.scale || 1));
            const tgtMax = mappingConfig.targetMax ?? (control.max * (control.scale || 1));

            mappingHTML = `
              <div class="audio-mapping-row">
                <button type="button" class="audio-mapping-toggle ${isEnabled ? 'active' : ''}"
                        data-mapping-param="${control.id}" title="Map to audio">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 18V5l12-2v13"/>
                    <circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                  </svg>
                </button>
                <div class="audio-mapping-panel ${isEnabled ? 'expanded' : ''}" data-mapping-param="${control.id}">
                  <select class="mapping-source" data-mapping-param="${control.id}">
                    <option value="bands.subBass" ${source === 'bands.subBass' ? 'selected' : ''}>Sub Bass (20-60Hz)</option>
                    <option value="bands.bass" ${source === 'bands.bass' ? 'selected' : ''}>Bass (60-250Hz)</option>
                    <option value="bands.lowMid" ${source === 'bands.lowMid' ? 'selected' : ''}>Low Mid (250-500Hz)</option>
                    <option value="bands.mid" ${source === 'bands.mid' ? 'selected' : ''}>Mid (500-2kHz)</option>
                    <option value="bands.highMid" ${source === 'bands.highMid' ? 'selected' : ''}>High Mid (2-4kHz)</option>
                    <option value="bands.presence" ${source === 'bands.presence' ? 'selected' : ''}>Presence (4-6kHz)</option>
                    <option value="bands.brilliance" ${source === 'bands.brilliance' ? 'selected' : ''}>Brilliance (6-20kHz)</option>
                    <option value="spectral.centroid" ${source === 'spectral.centroid' ? 'selected' : ''}>Brightness</option>
                    <option value="spectral.flux" ${source === 'spectral.flux' ? 'selected' : ''}>Spectral Flux</option>
                    <option value="spectral.flatness" ${source === 'spectral.flatness' ? 'selected' : ''}>Noisiness</option>
                    <option value="rhythm.onsetStrength" ${source === 'rhythm.onsetStrength' ? 'selected' : ''}>Onset Strength</option>
                  </select>
                  <div class="mapping-ranges">
                    <div class="mapping-range-row">
                      <span class="mapping-label">Audio:</span>
                      <input type="number" class="mapping-src-min" data-mapping-param="${control.id}" value="${srcMin}" step="0.1" min="0" max="1">
                      <span class="mapping-arrow">→</span>
                      <input type="number" class="mapping-src-max" data-mapping-param="${control.id}" value="${srcMax}" step="0.1" min="0" max="1">
                    </div>
                    <div class="mapping-range-row">
                      <span class="mapping-label">Visual:</span>
                      <input type="number" class="mapping-tgt-min" data-mapping-param="${control.id}" value="${tgtMin}" step="0.1">
                      <span class="mapping-arrow">→</span>
                      <input type="number" class="mapping-tgt-max" data-mapping-param="${control.id}" value="${tgtMax}" step="0.1">
                    </div>
                  </div>
                </div>
              </div>
            `;
          }

          return `
            <div class="control-group ${control.mappable ? 'has-mapping' : ''}">
              <div class="control-header">
                <label for="${control.id}">${control.label}: <span id="${control.id}Value">${displayValue}</span>${control.unit || ''}</label>
                ${mappingHTML}
              </div>
              <input type="range" id="${control.id}" min="${control.min}" max="${control.max}" value="${sliderValue}" data-scale="${control.scale || 1}">
            </div>
          `;

        case 'color':
          return `
            <div class="control-group">
              <label for="${control.id}">${control.label}:</label>
              <input type="color" id="${control.id}" value="${value}">
            </div>
          `;

        case 'checkbox':
          return `
            <div class="control-group">
              <label>
                <input type="checkbox" id="${control.id}" ${value ? 'checked' : ''}>
                ${control.label}
              </label>
            </div>
          `;

        case 'select':
          const optionsHTML = control.options.map(opt =>
            `<option value="${opt.value}" ${opt.value === value ? 'selected' : ''}>${opt.label}</option>`
          ).join('');
          return `
            <div class="control-group">
              <label for="${control.id}">${control.label}:</label>
              <select id="${control.id}">${optionsHTML}</select>
            </div>
          `;

        case 'custom':
          return `<div class="control-group control-custom" id="${control.id}-container"></div>`;

        default:
          return '';
      }
    }).join('');

    // First section open by default
    const isOpen = index === 0 ? 'open' : '';

    return `
      <div class="accordion-section ${isOpen}" data-section="${sectionName}">
        <div class="accordion-header">
          <h4>${sectionName}</h4>
          <span class="accordion-icon"><i data-lucide="chevron-down"></i></span>
        </div>
        <div class="accordion-content">
          <div class="accordion-content-inner">
            ${controlsHTML}
          </div>
        </div>
      </div>
    `;
  }).join('');

  elements.dynamicControlsEl.innerHTML = sectionsHTML;

  // Post-render hook for custom controls (they manage their own DOM)
  configUI.forEach(control => {
    if (control.type === 'custom' && control.render) {
      const container = document.getElementById(`${control.id}-container`);
      if (container) {
        control.render(container, currentConfig, (id, val) => {
          managers.visualizerManager.setConfig({ [id]: val });
        });
      }
    }
  });

  // Re-initialize Lucide icons for dynamically added accordion icons
  if (window.lucide) {
    window.lucide.createIcons();
  }

  // Attach accordion toggle listeners (only to dynamically created sections)
  elements.dynamicControlsEl.querySelectorAll('.accordion-header').forEach(header => {
    header.addEventListener('click', () => {
      const section = header.parentElement;
      section.classList.toggle('open');
    });
  });

  // Attach control event listeners
  configUI.forEach(control => {
    if (control.type === 'custom') return; // Custom controls manage their own listeners
    const el = document.getElementById(control.id);
    if (!el) return;

    // Select and checkbox use 'change', range uses 'input' for real-time updates
    const eventType = (control.type === 'checkbox' || control.type === 'select') ? 'change' : 'input';
    el.addEventListener(eventType, (e) => {
      let value;
      if (control.type === 'checkbox') {
        value = e.target.checked;
      } else if (control.type === 'range') {
        value = control.scale ? parseInt(e.target.value) * control.scale : parseInt(e.target.value);
        const displayValue = control.scale ? value.toFixed(2) : value;
        document.getElementById(`${control.id}Value`).textContent = displayValue;
      } else {
        value = e.target.value;
      }

      managers.visualizerManager.setConfig({ [control.id]: value });
    });
  });

  // Attach audio mapping event listeners
  attachAudioMappingListeners(configUI);
}

/**
 * Attach event listeners for audio mapping controls
 * @param {Array} configUI - The config UI definition array
 */
function attachAudioMappingListeners(configUI) {
  // Toggle buttons - expand/collapse and enable/disable mapping
  elements.dynamicControlsEl.querySelectorAll('.audio-mapping-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const paramId = btn.dataset.mappingParam;
      const panel = elements.dynamicControlsEl.querySelector(`.audio-mapping-panel[data-mapping-param="${paramId}"]`);
      const isActive = btn.classList.toggle('active');

      if (panel) {
        panel.classList.toggle('expanded', isActive);
      }

      // Update mapping config
      updateMappingConfig(paramId, { enabled: isActive });
    });
  });

  // Source dropdown changes
  elements.dynamicControlsEl.querySelectorAll('.mapping-source').forEach(select => {
    select.addEventListener('change', (e) => {
      const paramId = select.dataset.mappingParam;
      updateMappingConfig(paramId, { source: e.target.value });
    });
  });

  // Source range inputs
  elements.dynamicControlsEl.querySelectorAll('.mapping-src-min').forEach(input => {
    input.addEventListener('change', (e) => {
      const paramId = input.dataset.mappingParam;
      updateMappingConfig(paramId, { sourceMin: parseFloat(e.target.value) || 0 });
    });
  });

  elements.dynamicControlsEl.querySelectorAll('.mapping-src-max').forEach(input => {
    input.addEventListener('change', (e) => {
      const paramId = input.dataset.mappingParam;
      updateMappingConfig(paramId, { sourceMax: parseFloat(e.target.value) || 1 });
    });
  });

  // Target range inputs
  elements.dynamicControlsEl.querySelectorAll('.mapping-tgt-min').forEach(input => {
    input.addEventListener('change', (e) => {
      const paramId = input.dataset.mappingParam;
      updateMappingConfig(paramId, { targetMin: parseFloat(e.target.value) || 0 });
    });
  });

  elements.dynamicControlsEl.querySelectorAll('.mapping-tgt-max').forEach(input => {
    input.addEventListener('change', (e) => {
      const paramId = input.dataset.mappingParam;
      updateMappingConfig(paramId, { targetMax: parseFloat(e.target.value) || 1 });
    });
  });
}

/**
 * Update a mapping configuration in the visualizer config
 * @param {string} paramId - The parameter ID (e.g., 'timeScale')
 * @param {object} updates - Partial mapping config updates
 */
function updateMappingConfig(paramId, updates) {
  const mappingKey = `${paramId}Mapping`;
  const currentConfig = managers.visualizerManager.getConfig();
  const currentMapping = currentConfig[mappingKey] || {};

  const newMapping = {
    ...currentMapping,
    ...updates
  };

  managers.visualizerManager.setConfig({ [mappingKey]: newMapping });
}

/**
 * Reset visualizer settings to their default values
 * Preserves the current visualizer type, only resets config
 */
export function resetToDefaults() {
  const configUI = managers.visualizerManager.getConfigUI();
  if (!configUI || configUI.length === 0) return;

  // Extract defaults from configUI (handles scale factors)
  const uiDefaults = extractConfigDefaults(configUI);

  // Get visualizer-specific defaults from getDefaultConfig()
  const visualizer = managers.visualizerManager.getVisualizer();
  const visualizerDefaults = visualizer?.getDefaultConfig?.() || {};

  // Merge: UI defaults take precedence over visualizer defaults
  const defaults = {
    ...visualizerDefaults,
    ...uiDefaults
  };

  // Apply the defaults
  managers.visualizerManager.setConfig(defaults);

  // Rebuild UI to reflect new values
  buildControlsUI();

  // Clear and redraw the canvas
  managers.visualizerManager.clear();
}

/**
 * Recalculate lyrics scroll params when canvas or settings change
 */
export function recalculateLyricsScrollParams() {
  if (playlistState.currentTrackIndex >= 0 && playlistState.currentTrackIndex < playlistState.playlist.length) {
    const track = playlistState.playlist[playlistState.currentTrackIndex];
    if (track.lyrics && track.duration) {
      // Use overlay canvas dimensions for lyrics calculations
      // Pass alignedLyrics if available for time-synced highlighting
      lyricsState.currentLyricsScrollParams = managers.lyricsRenderer.calculateScrollParams(
        track.lyrics,
        track.duration,
        elements.lyricsOverlayCanvas.width,
        elements.lyricsOverlayCanvas.height,
        track.alignedLyrics
      );
    }
  }
}

/**
 * Set up visualization type change handler
 */
export function setupVizTypeChangeHandler() {
  // Legacy dropdown handler
  if (elements.vizTypeSelect) {
    elements.vizTypeSelect.addEventListener('change', async (e) => {
      const selectedType = e.target.value;
      await selectVisualizer(selectedType);
    });
  }
}

/**
 * Build visualizer grid for overlay panel
 */
export async function buildVisualizerGrid() {
  const grid = elements.visualizerGrid;
  if (!grid) return;

  // Exit reorder mode if grid is being rebuilt (e.g., visibility toggle during reorder)
  if (reorderMode) {
    reorderMode = false;
    grid.classList.remove('reorder-mode');
    clearCardSelection(grid);
    const btn = document.querySelector('#visualizerPanel .reorder-btn');
    if (btn) {
      btn.classList.remove('reorder-btn-active');
      btn.title = 'Reorder Visualizers';
      btn.innerHTML = '<i data-lucide="grip-vertical"></i>';
    }
  }

  // Check admin status first (needed for registry filtering and admin UI)
  const isAdmin = await authService.isAdmin();

  // Pre-populate caches so getListSync() has correct data
  await Promise.all([
    VisualizerRegistry.fetchHiddenSet(),
    VisualizerRegistry.fetchVisualizerOrder()
  ]);

  // Get bundled + premium visualizers synchronously (pass isAdmin for hidden filtering)
  const vizList = VisualizerRegistry.getListSync(isAdmin);
  const allowedViz = await entitlementService.getAllowedVisualizers();
  const currentType = managers.visualizerManager?.getType() || vizList[0]?.name;
  const canCreateCustom = await entitlementService.canCreateCustomVisualizers();
  const isFreeMode = await entitlementService.isFreeMode();

  // Get user visualizers async (includes user's own + public visualizers from others)
  let userVisualizers = [];
  let currentUserId = null;
  try {
    const fullList = await VisualizerRegistry.getList(true, isAdmin);
    userVisualizers = fullList.filter(v => v.source === 'user');
    // Get current user ID to determine ownership
    currentUserId = await visualizerLoader.getCurrentUserId();
  } catch (e) {
    console.warn('[VisualizerControls] Could not load user visualizers:', e);
  }

  // Build bundled/premium visualizers
  // In free mode (or wildcard '*'), all visualizers are unlocked
  const allAllowed = isFreeMode || allowedViz.includes('*');

  let html = vizList.map(viz => {
    const isLocked = !allAllowed && !allowedViz.includes(viz.name);
    const isActive = viz.name === currentType;
    const lockedClass = isLocked ? 'locked' : '';
    const activeClass = isActive ? 'active' : '';
    const isPremium = viz.source === 'premium' || viz.premium;
    const isBundled = viz.source === 'bundled' || (!viz.source && !isPremium);
    const source = viz.source || 'bundled';
    const contextType = viz.contextType || '2d';

    // Show clone button for premium visualizers (that user can access) AND bundled visualizers
    const showCloneBtn = canCreateCustom && ((isPremium && !isLocked) || isBundled);

    // Use div with role="button" instead of <button> to allow nested action buttons (clone)
    // Nested <button> elements are invalid HTML and break the grid layout
    const hiddenClass = viz.hidden ? 'viz-card-hidden' : '';
    return `
      <div class="viz-card ${lockedClass} ${activeClass} ${hiddenClass}" role="button" tabindex="0"
              data-viz="${viz.name}"
              data-source="${source}"
              data-context-type="${contextType}"
              data-display-name="${escapeAttr(viz.displayName)}"
              data-description="${escapeAttr(viz.description || '')}"
              ${isLocked ? 'data-locked="true"' : ''}>
        ${isPremium ? '<span class="viz-card-badge pro">Pro</span>' : ''}
        ${viz.hidden ? '<span class="viz-card-badge hidden">Hidden</span>' : ''}
        ${showCloneBtn ? `
          <div class="viz-card-actions">
            <button class="viz-card-clone" data-clone-viz="${viz.name}" data-clone-name="${escapeAttr(viz.displayName)}" data-clone-source="${source}" title="Clone & Edit">
              <i data-lucide="copy"></i>
            </button>
          </div>
        ` : ''}
        ${isAdmin ? `
          <div class="viz-card-admin-actions">
            <button class="viz-card-visibility" data-visibility-viz="${viz.name}" data-hidden="${viz.hidden ? 'true' : 'false'}" title="${viz.hidden ? 'Show visualizer' : 'Hide visualizer'}">
              <i data-lucide="${viz.hidden ? 'eye-off' : 'eye'}"></i>
            </button>
            <button class="viz-card-regenerate" data-regenerate-viz="${viz.name}" title="Regenerate Preview (Admin)">
              <i data-lucide="refresh-cw"></i>
            </button>
          </div>
        ` : ''}
        <div class="viz-card-preview" data-viz-name="${viz.name}">
          <div class="viz-card-fallback">
            <i data-lucide="${getVisualizerIcon(viz.name)}"></i>
          </div>
          <img class="viz-card-thumbnail" alt="${escapeAttr(viz.displayName)} preview" />
          <video class="viz-card-video" muted loop playsinline></video>
          <div class="viz-card-loading">
            <span class="spinner"></span>
          </div>
        </div>
        <span class="viz-card-name">${escapeHtml(viz.displayName)}</span>
        ${isLocked ? '<span class="viz-card-lock"><i data-lucide="lock"></i></span>' : ''}
      </div>
    `;
  }).join('');

  // Add section divider and custom visualizers if user can create OR there are any visualizers to show
  if (canCreateCustom || userVisualizers.length > 0) {
    html += `
      <div class="viz-section-header">
        <span>Custom Visualizers</span>
      </div>
    `;

    // Only show create button if user can create custom visualizers
    if (canCreateCustom) {
      html += `
        <button class="viz-create-btn" id="createCustomVizBtn" title="Create a custom visualizer with AI">
          <i data-lucide="sparkles"></i>
          <span>Create New</span>
        </button>
      `;
    }

    // Add user visualizers (owned + public from others)
    if (userVisualizers.length > 0) {
      html += userVisualizers.map(viz => {
        const isActive = viz.name === currentType;
        const activeClass = isActive ? 'active' : '';
        // Check if current user owns this visualizer
        const isOwner = currentUserId && viz.user_id === currentUserId;
        // Locked if user doesn't own it (unless free mode is enabled)
        const isLocked = !isOwner && !isFreeMode;
        const lockedClass = isLocked ? 'locked' : '';

        const userVizHiddenClass = viz.hidden ? 'viz-card-hidden' : '';
        return `
          <div class="viz-card viz-card-user ${activeClass} ${lockedClass} ${userVizHiddenClass}" role="button" tabindex="0"
               data-viz="${viz.name}" data-viz-id="${viz.id}" data-source="user"
               data-context-type="${viz.context_type || '2d'}"
               data-user-id="${viz.user_id || ''}"
               ${isLocked ? 'data-locked="true"' : ''}>
            <span class="viz-card-badge user">Custom</span>
            ${viz.hidden ? '<span class="viz-card-badge hidden">Hidden</span>' : ''}
            ${isOwner ? `
              <div class="viz-card-actions">
                <button class="viz-card-edit" data-edit-viz="${viz.id}" data-edit-name="${escapeAttr(viz.display_name)}" title="Edit visualizer">
                  <i data-lucide="pencil"></i>
                </button>
                <button class="viz-card-delete" data-delete-viz="${viz.id}" title="Delete visualizer">
                  <i data-lucide="trash-2"></i>
                </button>
              </div>
            ` : ''}
            ${isAdmin ? `
              <div class="viz-card-admin-actions">
                <button class="viz-card-visibility" data-visibility-viz="${viz.name}" data-hidden="${viz.hidden ? 'true' : 'false'}" title="${viz.hidden ? 'Show visualizer' : 'Hide visualizer'}">
                  <i data-lucide="${viz.hidden ? 'eye-off' : 'eye'}"></i>
                </button>
                <button class="viz-card-regenerate" data-regenerate-viz="${viz.name}" title="Regenerate Preview (Admin)">
                  <i data-lucide="refresh-cw"></i>
                </button>
              </div>
            ` : ''}
            <div class="viz-card-preview" data-viz-name="${viz.name}">
              <div class="viz-card-fallback">
                <i data-lucide="${viz.context_type === 'webgpu' ? 'box' : 'wand-2'}"></i>
              </div>
              <img class="viz-card-thumbnail" alt="${escapeAttr(viz.displayName)} preview" />
              <video class="viz-card-video" muted loop playsinline></video>
              <div class="viz-card-loading">
                <span class="spinner"></span>
              </div>
            </div>
            <span class="viz-card-name">${escapeHtml(viz.displayName)}</span>
            ${isLocked ? '<span class="viz-card-lock"><i data-lucide="lock"></i></span>' : ''}
          </div>
        `;
      }).join('');
    } else if (canCreateCustom) {
      // Only show empty state if user can create but hasn't yet
      html += `<div class="viz-empty-state">No custom visualizers yet</div>`;
    }
  }

  grid.innerHTML = html;

  // Re-initialize Lucide icons for dynamically added elements
  if (window.lucide) {
    window.lucide.createIcons();
  }

  // Set up hover handlers for video previews (these don't trigger until hover)
  setupVideoPreviewHandlers(grid);

  // Add click handlers to grid cards
  grid.querySelectorAll('.viz-card').forEach(card => {
    const handleSelect = async (e) => {
      // In reorder mode, clicks handle multi-select instead of visualizer switching
      if (reorderMode) {
        handleReorderClick(e, card, grid);
        return;
      }
      // Don't trigger if clicking any action button (edit, clone, delete, admin actions)
      if (e.target.closest('.viz-card-actions') || e.target.closest('.viz-card-admin-actions')) return;

      const vizName = card.dataset.viz;
      const source = card.dataset.source || 'auto';
      const isLocked = card.dataset.locked === 'true';
      await selectVisualizer(vizName, source, isLocked);
    };

    card.addEventListener('click', handleSelect);

    // Add keyboard support for div-based cards (Enter/Space)
    if (card.getAttribute('role') === 'button') {
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect(e);
        }
      });
    }
  });

  // Add click handler for create button
  const createBtn = grid.querySelector('#createCustomVizBtn');
  if (createBtn) {
    createBtn.addEventListener('click', () => {
      showCustomVizModal();
    });
  }

  // Add click handlers for delete buttons
  grid.querySelectorAll('.viz-card-delete').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const vizId = btn.dataset.deleteViz;
      await handleDeleteVisualizer(vizId);
    });
  });

  // Add click handlers for edit buttons (user visualizers)
  grid.querySelectorAll('.viz-card-edit').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const vizId = btn.dataset.editViz;
      const vizName = btn.dataset.editName;
      const card = btn.closest('.viz-card');
      const contextType = card?.dataset.contextType || '2d';
      await handleEditVisualizer(vizId, vizName, contextType);
    });
  });

  // Add click handlers for clone buttons (premium visualizers)
  grid.querySelectorAll('.viz-card-clone').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const vizName = btn.dataset.cloneViz;
      const displayName = btn.dataset.cloneName;
      const source = btn.dataset.cloneSource;
      const card = btn.closest('.viz-card');
      const contextType = card?.dataset.contextType || '2d';
      await handleCloneVisualizer(vizName, displayName, source, contextType);
    });
  });

  // Add click handlers for regenerate buttons (admin only)
  grid.querySelectorAll('.viz-card-regenerate').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const vizName = btn.dataset.regenerateViz;
      await handleRegenerateThumbnail(vizName);
    });
  });

  // Add click handlers for visibility toggle buttons (admin only)
  grid.querySelectorAll('.viz-card-visibility').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const vizName = btn.dataset.visibilityViz;
      await handleToggleVisibility(vizName);
    });
  });

  // Inject admin reorder button into panel header (if admin)
  if (isAdmin) {
    injectReorderButton(grid);
    setupDragAndDrop(grid);
  }

  // Set up listener to initialize thumbnails when panel opens (only once)
  if (!thumbnailsInitialized) {
    document.addEventListener('panelOpened', (e) => {
      if (e.detail?.panel === 'visualizer') {
        initializeThumbnailsOnPanelOpen();
      }
    });

    // Stop all videos when any panel closes (cleanup)
    document.addEventListener('panelsClosed', () => {
      stopAllVideoPreviews(grid);
    });

    // Listen for custom visualizer select events
    document.addEventListener('selectVisualizer', async (e) => {
      const { name, source } = e.detail;
      await selectVisualizer(name, source);
    });

    // Initialize custom visualizer module
    initCustomVisualizer({
      onCreated: () => {
        // Rebuild grid when new visualizer is created
        buildVisualizerGrid();
      }
    });

    thumbnailsInitialized = true;
  }

  // If the visualizer panel is already open, initialize thumbnails immediately
  // This handles the case when grid is rebuilt while panel is open (e.g., after creating custom visualizer)
  if (getActivePanel() === 'visualizer') {
    setupThumbnailLazyLoading(grid);
  }
}

/**
 * Handle delete visualizer confirmation and deletion
 */
async function handleDeleteVisualizer(vizId) {
  const confirmed = confirm('Are you sure you want to delete this custom visualizer? This action cannot be undone.');
  if (!confirmed) return;

  try {
    await deleteVisualizer(vizId);
    // Rebuild the grid
    await buildVisualizerGrid();
  } catch (error) {
    console.error('[VisualizerControls] Failed to delete visualizer:', error);
    alert(`Failed to delete visualizer: ${error.message}`);
  }
}

/**
 * Handle edit visualizer button click
 * @param {string} vizId - The visualizer ID
 * @param {string} vizName - The visualizer display name
 * @param {string} contextType - '2d' or 'webgpu'
 */
async function handleEditVisualizer(vizId, vizName, contextType) {
  try {
    // Fetch the existing code
    const { code } = await fetchVisualizerCode(vizId, 'user');

    // Open the edit modal
    showEditModal({
      mode: 'edit',
      visualizerId: vizId,
      visualizerName: vizName,
      existingCode: code,
      contextType: contextType
    });
  } catch (error) {
    console.error('[VisualizerControls] Failed to fetch visualizer for editing:', error);
    alert(`Failed to load visualizer for editing: ${error.message}`);
  }
}

/**
 * Handle clone visualizer button click
 * Supports both client-side and server-side code fetching
 * @param {string} vizName - The visualizer name
 * @param {string} displayName - The visualizer display name
 * @param {string} source - 'bundled' or 'premium'
 * @param {string} contextType - '2d' or 'webgpu'
 */
async function handleCloneVisualizer(vizName, displayName, source, contextType) {
  try {
    // Fetch the existing code (or get info for server-side fetching)
    const fetchResult = await fetchVisualizerCode(vizName, source);

    // Open the clone modal with appropriate options
    const modalOptions = {
      mode: 'clone',
      visualizerId: vizName,
      visualizerName: displayName,
      contextType: fetchResult.contextType || contextType
    };

    if (fetchResult.useServerFetch) {
      // Use server-side fetching (for bundled visualizers)
      modalOptions.sourceType = source;
      modalOptions.sourceName = vizName;
    } else {
      // Use client-side code
      modalOptions.existingCode = fetchResult.code;
    }

    showEditModal(modalOptions);
  } catch (error) {
    console.error('[VisualizerControls] Failed to fetch visualizer for cloning:', error);
    alert(`Failed to load visualizer for cloning: ${error.message}`);
  }
}

/**
 * Handle regenerate thumbnail button click (admin only)
 * Deletes existing thumbnails from storage and regenerates with admin's current config
 * @param {string} vizName - The visualizer name
 */
async function handleRegenerateThumbnail(vizName) {
  // Capture video playback position before the confirm dialog pauses it
  const card = document.querySelector(`[data-viz="${vizName}"]`);
  const videoEl = card?.querySelector('.viz-card-video');
  const capturedTime = (videoEl && !videoEl.paused && videoEl.readyState >= 2)
    ? videoEl.currentTime
    : null;

  const timeInfo = capturedTime !== null
    ? `\nSnapshot will be captured at ${capturedTime.toFixed(2)}s (the moment you see now).`
    : `\nTip: hover over the card to play the preview, then click regenerate at the best-looking moment.`;

  const confirmed = confirm(
    `Regenerate thumbnail and video for "${vizName}"?\n\n` +
    `This will delete the current previews and regenerate them with your current visualizer settings.` +
    timeInfo
  );
  if (!confirmed) return;

  try {
    const session = authService.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    // Show loading state on the card (reuse card ref from above)
    const previewEl = card?.querySelector('.viz-card-preview');
    const loadingEl = previewEl?.querySelector('.viz-card-loading');
    if (loadingEl) {
      loadingEl.classList.add('active');
    }

    // Delete both static and preview from storage via admin function
    for (const type of ['static', 'preview']) {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-regenerate-thumbnail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ visualizerName: vizName, type })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to delete ${type}`);
      }
    }

    // Clear local cache for this visualizer
    thumbnailService.clearCacheFor(vizName);

    // Regenerate with admin's current config (gives admin full control)
    const currentConfig = managers.visualizerManager?.getConfig() || {};

    // Generate and upload static thumbnail at the captured animation time
    const offsetInfo = capturedTime !== null ? ` at time offset ${capturedTime.toFixed(2)}s` : '';
    console.log(`[VisualizerControls] Regenerating static thumbnail for ${vizName}${offsetInfo}...`);
    const staticBlob = await thumbnailGenerator.generateStaticThumbnail(vizName, null, currentConfig, capturedTime);
    await thumbnailService.uploadToStorage(vizName, 'static', staticBlob);
    console.log(`[VisualizerControls] Static thumbnail uploaded for ${vizName}`);

    // Update the thumbnail image immediately
    const imgEl = previewEl?.querySelector('.viz-card-thumbnail');
    if (imgEl) {
      const blobUrl = URL.createObjectURL(staticBlob);
      imgEl.onload = () => URL.revokeObjectURL(blobUrl);
      imgEl.src = blobUrl;
      imgEl.classList.add('loaded');
      const fallbackEl = previewEl?.querySelector('.viz-card-fallback');
      if (fallbackEl) {
        fallbackEl.classList.add('hidden');
      }
    }

    // Generate video in background
    thumbnailGenerator.generatePreviewVideo(vizName, null, currentConfig)
      .then(async (videoBlob) => {
        await thumbnailService.uploadToStorage(vizName, 'preview', videoBlob);
        console.log(`[VisualizerControls] Video preview uploaded for ${vizName}`);

        // Update video element if card is still in DOM
        const updatedCard = document.querySelector(`[data-viz="${vizName}"]`);
        const videoEl = updatedCard?.querySelector('.viz-card-video');
        if (videoEl) {
          const videoBlobUrl = URL.createObjectURL(videoBlob);
          videoEl.onloadeddata = () => URL.revokeObjectURL(videoBlobUrl);
          videoEl.src = videoBlobUrl;
          console.log(`[VisualizerControls] Video element updated for ${vizName}`);
        }
      })
      .catch(err => {
        console.warn(`[VisualizerControls] Video preview generation failed for ${vizName}:`, err);
      });

    // Hide loading state
    if (loadingEl) {
      loadingEl.classList.remove('active');
    }

    alert('Thumbnail regenerated successfully! Video preview is generating in the background.');

  } catch (error) {
    console.error('[VisualizerControls] Failed to regenerate thumbnail:', error);
    alert(`Failed to regenerate thumbnail: ${error.message}`);

    // Hide loading state on error
    const card = document.querySelector(`[data-viz="${vizName}"]`);
    const loadingEl = card?.querySelector('.viz-card-loading');
    if (loadingEl) {
      loadingEl.classList.remove('active');
    }
  }
}

/**
 * Handle visibility toggle button click (admin only)
 * Toggles whether a visualizer is hidden from non-admin users
 * @param {string} vizName - The visualizer name
 */
async function handleToggleVisibility(vizName) {
  try {
    const supabase = authService.getClient();
    if (!supabase) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase.rpc('toggle_visualizer_visibility', {
      p_viz_name: vizName
    });

    if (error) {
      throw new Error(error.message);
    }

    console.log(`[VisualizerControls] Toggled visibility for "${vizName}": hidden=${data.hidden}`);

    // Invalidate caches and rebuild grid
    VisualizerRegistry._hiddenVisualizersCache = null;
    VisualizerRegistry._listCache = null;
    await buildVisualizerGrid();
  } catch (error) {
    console.error('[VisualizerControls] Failed to toggle visibility:', error);
    alert(`Failed to toggle visibility: ${error.message}`);
  }
}

/**
 * Stop all video previews in the grid
 * @param {HTMLElement} grid
 */
function stopAllVideoPreviews(grid) {
  if (!grid) return;

  // Clear all hover states
  hoveredCards.clear();

  // Clear all pending timeouts
  hoverTimeouts.forEach(timeout => clearTimeout(timeout));
  hoverTimeouts.clear();

  // Stop all videos
  grid.querySelectorAll('.viz-card-video').forEach(video => {
    video.pause();
    video.currentTime = 0;
    video.classList.remove('playing');
    video.onloadeddata = null;
  });
}

/**
 * Get icon name for a visualizer type
 */
function getVisualizerIcon(vizName) {
  const iconMap = {
    'bars': 'bar-chart-3',
    'bubbles': 'circle-dot',
    'waveform': 'audio-waveform',
    'spectrum': 'waves',
    'particles': 'sparkles',
    'circles': 'target',
    'tunnel': 'move-3d',
    'blob': 'cloud'
  };
  return iconMap[vizName] || 'audio-lines';
}

/**
 * Select a visualizer and update UI
 * @param {string} vizName - The visualizer name
 * @param {string} source - Source: 'bundled', 'premium', 'user', or 'auto'
 * @param {boolean} isLocked - Whether the visualizer is locked (for custom visualizers)
 */
async function selectVisualizer(vizName, source = 'auto', isLocked = false) {
  // Handle locked custom visualizers (public visualizers from other users)
  if (source === 'user' && isLocked) {
    // Get display name from the full list
    const fullList = await VisualizerRegistry.getList(true);
    const viz = fullList.find(v => v.name === vizName);
    const displayName = viz ? viz.displayName : vizName;

    // Show paywall prompting login/upgrade to access custom visualizers
    featureGate.showPaywall({
      feature: 'custom-visualizer',
      title: 'Login Required',
      message: `Sign in to use the "${displayName}" custom visualizer, or upgrade to Pro to create your own.`,
      ctaText: 'Sign In'
    });
    return;
  }

  // Check access for bundled/premium visualizers
  if (source !== 'user') {
    // Check if user can access this visualizer
    const canAccess = await featureGate.canAccessVisualizer(vizName);
    if (!canAccess) {
      // Get display name for the visualizer
      const vizList = VisualizerRegistry.getListSync();
      const viz = vizList.find(v => v.name === vizName);
      const displayName = viz ? viz.displayName : vizName;

      // Show paywall
      featureGate.showPaywall({
        feature: 'visualizer',
        title: 'Upgrade Required',
        message: `The "${displayName}" visualizer is available on the Pro plan.`,
        ctaText: 'Upgrade to Pro'
      });
      return;
    }
  }

  try {
    // Set the visualizer type with source hint
    await managers.visualizerManager.setType(vizName, source);
    buildControlsUI();
    managers.visualizerManager.clear();

    // Track visualizer selection
    const isPremium = source === 'premium' || source === 'user';
    trackVisualizerSelected(vizName, isPremium);

    // Update grid selection
    updateVisualizerGridSelection(vizName);

    // Update legacy dropdown if present
    if (elements.vizTypeSelect) {
      elements.vizTypeSelect.value = vizName;
    }

    // Recalculate lyrics scroll params for new canvas/visualizer
    recalculateLyricsScrollParams();

    // Close the visualizer panel after selection
    closePanelOnSelect('visualizer');
  } catch (error) {
    console.error('[VisualizerControls] Failed to load visualizer:', error);
    alert(`Failed to load visualizer: ${error.message}`);
  }
}

/**
 * Update visualizer grid selection state
 */
export function updateVisualizerGridSelection(vizName) {
  const grid = elements.visualizerGrid;
  if (!grid) return;

  grid.querySelectorAll('.viz-card').forEach(card => {
    if (card.dataset.viz === vizName) {
      card.classList.add('active');
    } else {
      card.classList.remove('active');
    }
  });
}

/**
 * Initialize thumbnail loading when visualizer panel opens
 * This ensures thumbnails only load when the panel is visible
 */
function initializeThumbnailsOnPanelOpen() {
  const grid = elements.visualizerGrid;
  if (!grid) return;

  // Set up the IntersectionObserver now that the panel is open
  setupThumbnailLazyLoading(grid);
}

/**
 * Set up IntersectionObserver for lazy loading static thumbnails
 * @param {HTMLElement} grid
 */
function setupThumbnailLazyLoading(grid) {
  // Clean up existing observer
  if (thumbnailObserver) {
    thumbnailObserver.disconnect();
  }

  const previews = grid.querySelectorAll('.viz-card-preview');

  // For small grids (< 20 items), load all thumbnails immediately
  // This is more reliable than IntersectionObserver for the initial view
  if (previews.length < 20) {
    previews.forEach(preview => {
      const vizName = preview.dataset.vizName;
      if (vizName) {
        loadStaticThumbnail(preview, vizName);
      }
    });
    return;
  }

  // For larger grids, use IntersectionObserver for lazy loading
  thumbnailObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const preview = entry.target;
        const vizName = preview.dataset.vizName;
        if (vizName) {
          loadStaticThumbnail(preview, vizName);
          thumbnailObserver.unobserve(entry.target);
        }
      }
    });
  }, {
    root: grid.closest('.panel-content'),
    rootMargin: '50px', // Start loading slightly before visible
    threshold: 0.1
  });

  previews.forEach(preview => {
    thumbnailObserver.observe(preview);
  });
}

/**
 * Load static thumbnail for a visualizer card
 * @param {HTMLElement} previewEl
 * @param {string} vizName
 */
async function loadStaticThumbnail(previewEl, vizName) {
  const img = previewEl.querySelector('.viz-card-thumbnail');
  const fallback = previewEl.querySelector('.viz-card-fallback');
  const loading = previewEl.querySelector('.viz-card-loading');

  if (!img || !fallback || !loading) return;

  // Show loading indicator
  loading.classList.add('active');

  try {
    const result = await thumbnailService.getStaticThumbnail(vizName);

    if (result.url) {
      img.src = result.url;
      img.onload = () => {
        img.classList.add('loaded');
        fallback.classList.add('hidden');
        loading.classList.remove('active');
      };
      img.onerror = () => {
        // Keep fallback visible
        loading.classList.remove('active');
        console.warn(`[VisualizerControls] Failed to load thumbnail for ${vizName}`);
      };
    } else {
      // No thumbnail available, keep icon
      loading.classList.remove('active');
    }
  } catch (error) {
    console.warn(`[VisualizerControls] Failed to get thumbnail for ${vizName}:`, error);
    loading.classList.remove('active');
  }
}

/**
 * Set up hover handlers for video preview loading
 * @param {HTMLElement} grid
 */
function setupVideoPreviewHandlers(grid) {
  grid.querySelectorAll('.viz-card').forEach(card => {
    const preview = card.querySelector('.viz-card-preview');
    if (!preview) return;

    const vizName = preview.dataset.vizName;
    if (!vizName) return;

    card.addEventListener('mouseenter', () => {
      // Track that this card is being hovered
      hoveredCards.add(vizName);

      // Debounce: wait 200ms before loading video to avoid loading on quick mouse passes
      const timeout = setTimeout(() => {
        // Double-check we're still hovering before loading
        if (hoveredCards.has(vizName)) {
          loadVideoPreview(preview, vizName);
        }
      }, 200);
      hoverTimeouts.set(vizName, timeout);
    });

    card.addEventListener('mouseleave', () => {
      // Track that this card is no longer hovered
      hoveredCards.delete(vizName);

      // Cancel pending load
      const timeout = hoverTimeouts.get(vizName);
      if (timeout) {
        clearTimeout(timeout);
        hoverTimeouts.delete(vizName);
      }

      // Stop and hide video
      const video = preview.querySelector('.viz-card-video');
      const img = preview.querySelector('.viz-card-thumbnail');
      const fallback = preview.querySelector('.viz-card-fallback');

      if (video) {
        video.pause();
        video.currentTime = 0; // Reset to start
        video.classList.remove('playing');
        // Clear handlers to prevent stale callbacks from playing
        video.onloadeddata = null;
      }

      // Show thumbnail if it has a valid src, otherwise show fallback
      if (img && img.src && img.complete && img.naturalWidth > 0) {
        img.classList.add('loaded');
        if (fallback) fallback.classList.add('hidden');
      } else if (fallback) {
        fallback.classList.remove('hidden');
      }
    });
  });
}

/**
 * Load video preview on hover
 * @param {HTMLElement} previewEl
 * @param {string} vizName
 */
async function loadVideoPreview(previewEl, vizName) {
  const video = previewEl.querySelector('.viz-card-video');
  const loading = previewEl.querySelector('.viz-card-loading');

  if (!video) return;

  // Check if still hovered before proceeding
  if (!hoveredCards.has(vizName)) return;

  // If video is already loaded, just play it (if still hovered)
  if (video.src && video.readyState >= 2) {
    if (hoveredCards.has(vizName)) {
      video.classList.add('playing');
      video.play().catch(() => {});
    }
    return;
  }

  // Show loading indicator
  if (loading) loading.classList.add('active');

  try {
    const result = await thumbnailService.getPreviewVideo(vizName);

    // Check if still hovered after async operation
    if (!hoveredCards.has(vizName)) {
      if (loading) loading.classList.remove('active');
      return;
    }

    if (result.url) {
      video.src = result.url;
      video.onloadeddata = () => {
        // Check if still hovered when video loads
        if (hoveredCards.has(vizName)) {
          video.classList.add('playing');
          video.play().catch(() => {});
        } else {
          // Not hovered anymore, stop immediately
          video.pause();
          video.currentTime = 0;
          video.classList.remove('playing');
        }
        if (loading) loading.classList.remove('active');
      };
      video.onerror = () => {
        if (loading) loading.classList.remove('active');
        console.warn(`[VisualizerControls] Failed to load video preview for ${vizName}`);
      };
    } else {
      if (loading) loading.classList.remove('active');
    }
  } catch (error) {
    console.warn(`[VisualizerControls] Failed to get video preview for ${vizName}:`, error);
    if (loading) loading.classList.remove('active');
  }
}

// ============================================
// Admin Reorder Mode
// ============================================

/**
 * Inject the reorder toggle button into the visualizer panel header.
 * Avoids duplicates on grid rebuild.
 */
function injectReorderButton(grid) {
  const header = document.querySelector('#visualizerPanel .panel-header');
  if (!header || header.querySelector('.reorder-btn')) return;

  const btn = document.createElement('button');
  btn.className = 'reorder-btn';
  btn.title = 'Reorder Visualizers';
  btn.innerHTML = '<i data-lucide="grip-vertical"></i>';

  const closeBtn = header.querySelector('.panel-close-btn');
  header.insertBefore(btn, closeBtn);

  if (window.lucide) window.lucide.createIcons({ nodes: [btn] });

  btn.addEventListener('click', (e) => {
    // Stop propagation: lucide.createIcons() inside toggleReorderMode replaces the SVG
    // click target, orphaning it from the DOM. The document-level click handler then
    // sees e.target outside the panel and closes it. Same pattern as icon sidebar buttons.
    e.stopPropagation();
    toggleReorderMode(grid, btn);
  });
}

/**
 * Toggle reorder mode on/off.
 * On exit, saves the new order to the backend.
 */
async function toggleReorderMode(grid, btn) {
  reorderMode = !reorderMode;

  if (reorderMode) {
    // Enter reorder mode
    btn.classList.add('reorder-btn-active');
    btn.title = 'Save Order';
    btn.innerHTML = '<i data-lucide="check"></i>';
    grid.classList.add('reorder-mode');

    // Clear any previous selection state
    clearCardSelection(grid);

    // Make non-user cards draggable
    grid.querySelectorAll('.viz-card:not(.viz-card-user)').forEach(card => {
      card.setAttribute('draggable', 'true');
    });
  } else {
    // Exit reorder mode — save first
    btn.classList.remove('reorder-btn-active');
    btn.title = 'Reorder Visualizers';
    btn.innerHTML = '<i data-lucide="grip-vertical"></i>';
    grid.classList.remove('reorder-mode');

    // Remove draggable attribute and selection
    grid.querySelectorAll('.viz-card[draggable]').forEach(card => {
      card.removeAttribute('draggable');
    });
    clearCardSelection(grid);

    await saveVisualizerOrder(grid);
  }

  if (window.lucide) window.lucide.createIcons({ nodes: [btn] });
}

/**
 * Handle click on a card during reorder mode for multi-select.
 * - Plain click: select single card (clear others)
 * - Ctrl/Cmd+click: toggle card in selection
 * - Shift+click: range select from anchor to clicked card
 */
function handleReorderClick(e, card, grid) {
  // Only handle non-user cards
  if (card.classList.contains('viz-card-user')) return;
  // Don't handle clicks on action buttons
  if (e.target.closest('.viz-card-actions') || e.target.closest('.viz-card-admin-actions')) return;

  const isCtrl = e.ctrlKey || e.metaKey;
  const isShift = e.shiftKey;

  if (isShift && selectionAnchor) {
    // Range select: from anchor to this card
    const cards = Array.from(grid.querySelectorAll('.viz-card:not(.viz-card-user)'));
    const anchorIdx = cards.indexOf(selectionAnchor);
    const clickIdx = cards.indexOf(card);
    const start = Math.min(anchorIdx, clickIdx);
    const end = Math.max(anchorIdx, clickIdx);

    // Clear current selection, then select range
    selectedCards.forEach(c => c.classList.remove('reorder-selected'));
    selectedCards.clear();

    for (let i = start; i <= end; i++) {
      selectedCards.add(cards[i]);
      cards[i].classList.add('reorder-selected');
    }
    // Keep anchor unchanged for subsequent shift-clicks
  } else if (isCtrl) {
    // Toggle this card in selection
    if (selectedCards.has(card)) {
      selectedCards.delete(card);
      card.classList.remove('reorder-selected');
    } else {
      selectedCards.add(card);
      card.classList.add('reorder-selected');
    }
    selectionAnchor = card;
  } else {
    // Plain click: select only this card
    clearCardSelection(grid);
    selectedCards.add(card);
    card.classList.add('reorder-selected');
    selectionAnchor = card;
  }
}

/**
 * Clear all card selection state.
 */
function clearCardSelection(grid) {
  selectedCards.forEach(c => c.classList.remove('reorder-selected'));
  selectedCards.clear();
  selectionAnchor = null;
}

/**
 * Get the ordered list of cards being dragged (selected cards, or just the dragged card).
 * Returns them in their current DOM order to preserve relative positions.
 */
function getDragGroup(grid) {
  if (selectedCards.size > 1 && selectedCards.has(draggedCard)) {
    // Multi-select drag: return selected cards in DOM order
    const allCards = Array.from(grid.querySelectorAll('.viz-card:not(.viz-card-user)'));
    return allCards.filter(c => selectedCards.has(c));
  }
  // Single card drag
  return [draggedCard];
}

/**
 * Set up HTML5 drag-and-drop event handlers on the grid (event delegation).
 * Supports multi-select: if multiple cards are selected, dragging any of them
 * moves the entire group while preserving their relative order.
 */
function setupDragAndDrop(grid) {
  grid.addEventListener('dragstart', (e) => {
    if (!reorderMode) return;
    const card = e.target.closest('.viz-card:not(.viz-card-user)');
    if (!card) return;

    draggedCard = card;

    // If dragging an unselected card, clear selection and select just this one
    if (!selectedCards.has(card)) {
      clearCardSelection(grid);
      selectedCards.add(card);
      card.classList.add('reorder-selected');
      selectionAnchor = card;
    }

    // Mark all cards in the drag group as dragging
    const group = getDragGroup(grid);
    group.forEach(c => c.classList.add('dragging'));

    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox — set some data
    e.dataTransfer.setData('text/plain', card.dataset.viz);
  });

  grid.addEventListener('dragover', (e) => {
    if (!reorderMode || !draggedCard) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const target = e.target.closest('.viz-card:not(.viz-card-user)');
    // Don't show indicator on cards that are part of the drag group
    if (!target || selectedCards.has(target)) {
      clearDropIndicators(grid);
      return;
    }

    clearDropIndicators(grid);

    // For a 2-column grid, use DOM order to determine before/after.
    // Compare against the first card in the drag group for consistent direction.
    const cards = Array.from(grid.querySelectorAll('.viz-card:not(.viz-card-user)'));
    const dragIdx = cards.indexOf(draggedCard);
    const targetIdx = cards.indexOf(target);

    if (dragIdx < targetIdx) {
      target.classList.add('drag-over-after');
    } else {
      target.classList.add('drag-over-before');
    }
  });

  grid.addEventListener('dragleave', (e) => {
    if (!reorderMode) return;
    const target = e.target.closest('.viz-card:not(.viz-card-user)');
    if (target) {
      target.classList.remove('drag-over-before', 'drag-over-after');
    }
  });

  grid.addEventListener('drop', (e) => {
    if (!reorderMode || !draggedCard) return;
    e.preventDefault();

    const target = e.target.closest('.viz-card:not(.viz-card-user)');
    if (!target || selectedCards.has(target)) {
      clearDropIndicators(grid);
      return;
    }

    const group = getDragGroup(grid);
    const insertBefore = target.classList.contains('drag-over-before');

    // Remove all group cards from the DOM first
    group.forEach(c => c.remove());

    // Determine insertion reference point
    if (insertBefore) {
      // Insert all group cards before target, in order
      group.forEach(c => grid.insertBefore(c, target));
    } else {
      // Insert all group cards after target, in order
      let ref = target;
      for (const c of group) {
        ref.insertAdjacentElement('afterend', c);
        ref = c;
      }
    }

    clearDropIndicators(grid);
  });

  grid.addEventListener('dragend', () => {
    // Remove dragging class from all cards in the group
    grid.querySelectorAll('.viz-card.dragging').forEach(c => {
      c.classList.remove('dragging');
    });
    draggedCard = null;
    clearDropIndicators(grid);
  });
}

/**
 * Remove all drop indicator classes from grid cards.
 */
function clearDropIndicators(grid) {
  grid.querySelectorAll('.drag-over-before, .drag-over-after').forEach(el => {
    el.classList.remove('drag-over-before', 'drag-over-after');
  });
}

/**
 * Read the current card order from the DOM and save it to the database.
 * Skips the RPC call if the order hasn't changed.
 */
async function saveVisualizerOrder(grid) {
  const cards = grid.querySelectorAll('.viz-card:not(.viz-card-user)');
  const newOrder = Array.from(cards).map(card => card.dataset.viz);

  // Compare with cached order to skip unnecessary saves
  const cachedOrder = VisualizerRegistry._visualizerOrderCache || [];
  if (newOrder.length === cachedOrder.length && newOrder.every((name, i) => name === cachedOrder[i])) {
    return;
  }

  try {
    const supabase = authService.getClient();
    if (!supabase) {
      throw new Error('Not authenticated');
    }

    const { error } = await supabase.rpc('save_visualizer_order', {
      p_order: newOrder
    });

    if (error) {
      throw new Error(error.message);
    }

    console.log(`[VisualizerControls] Saved visualizer order (${newOrder.length} items)`);

    // Update order cache directly (avoids staleness window if grid rebuilds before re-fetch)
    VisualizerRegistry._visualizerOrderCache = newOrder;
    // Invalidate list cache so next grid build re-applies the new order
    VisualizerRegistry._listCache = null;
  } catch (error) {
    console.error('[VisualizerControls] Failed to save visualizer order:', error);
    alert(`Failed to save visualizer order: ${error.message}`);
  }
}
